import React, { useCallback, useState } from "react";
import styled from "styled-components";

import type { FormFieldValueType } from "../../../../../../mobile/core/data-forms/form-field";
import type { FileConstraints, FileViolation } from "../../../../../../shared/core/data-forms/file-constraints";
import type {
  FileProps,
  FormCollectionInput,
  FormCollectionInputWithHierarchy,
  FormImageInput,
  FormInputWithHierarchy,
} from "../../../../../../shared/core/data-forms/form-input-types";
import { FormInputType } from "../../../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { isDefined } from "../../../../../../shared/utils/assert";
import { hasMultiplePages } from "../../../../../../shared/utils/form-input-image";
import type { UploadedImage } from "../../../../../../shared/utils/images-converter";
import { UnstyledButton } from "../../../../common/buttons/default-button";
import { FileConstraintsPanel } from "../../../../common/data-forms/file-constraints";
import { FormField } from "../../../../common/data-forms/form-field";
import FormLabel from "../../../../common/forms/form-label";
import { MarkdownText } from "../../../../common/forms/markdown-text";
import { theme } from "../../../../styles/theme";
import { UIConstants } from "../../../../styles/uiConstants";
import { FormWithButtons } from "../../shared/components/form-with-buttons";

const EnrollmentStepForm: React.FC<{
  disabled?: boolean;
  erroredFields?: string[];
  overrideActions?: React.ReactNode;
  step: FormCollectionInputWithHierarchy | FormCollectionInput;
  errorMessage?: string | null;
  withoutActions?: boolean;
  onInputValueChange: (input: FormInputWithHierarchy, value: FormFieldValueType, fileProps?: FileProps) => void;
  onInputMultiImagesChange: (input: FormInputWithHierarchy, values: UploadedImage[]) => void;
  onSubmit?: () => void;
  onBackward?: () => void;
}> = ({
  disabled,
  overrideActions,
  step,
  errorMessage,
  erroredFields,
  onInputValueChange,
  onInputMultiImagesChange,
  onSubmit,
  onBackward,
}) => {
  const { formatMessage } = useIntl();
  const [openConstraints, setOpenConstraints] = useState<string[]>([]);
  const [fileViolations, setFileViolations] = useState<Record<string, FileViolation[]>>({});

  const toggleConstraints = useCallback((id: string) => {
    setOpenConstraints((open) => (open.includes(id) ? open.filter((other) => other !== id) : [...open, id]));
  }, []);

  // A file that breaks the rules opens the panel on its own: the reason has to be in front of the
  // holder at the moment the file is picked, not after a submission.
  const reportViolations = useCallback((id: string, violations: FileViolation[]) => {
    setFileViolations((all) => ({ ...all, [id]: violations }));
    if (violations.length) {
      setOpenConstraints((open) => (open.includes(id) ? open : [...open, id]));
    }
  }, []);

  const nbImages = (input: FormInputWithHierarchy) => {
    const nbPicturesForDocumentText = (count: number) => {
      if (count === 1) {
        return "(" + formatMessage("registerScreen.nbPicturesForDocument.one") + ")";
      }
      return `(${formatMessage("registerScreen.nbPicturesForDocument.others", { count })})`;
    };

    if (input.type === FormInputType.Image) {
      const inputImage = input as FormImageInput;
      if (isDefined(inputImage.minimumPageCount)) {
        return hasMultiplePages(inputImage) ? nbPicturesForDocumentText(inputImage.minimumPageCount) : undefined;
      }
    }
    return undefined;
  };

  const inputHasError = (inputName) => (erroredFields ? erroredFields?.includes(inputName) : false);

  return (
    <FormWithButtons
      disabled={disabled}
      overrideActions={overrideActions}
      onSubmit={() => onSubmit?.()}
      errorMessage={errorMessage}
      onBackward={() => onBackward?.()}
    >
      {step.label && <StepTitle>{step.label}</StepTitle>}
      <InputsContainer>
        {step?.inputs?.map((input, index) => {
          if (input.disabled) {
            return null;
          }
          // An input without an id cannot be told from its neighbours, so its position stands in.
          const fieldId = input.id ?? String(index);
          const constraints = constraintsOf(input);
          const violations = fileViolations[fieldId] ?? [];
          const constraintsOpen = openConstraints.includes(fieldId);

          return (
            <Cell key={input.id} $wide={isWideInput(input)}>
              <LabelRow>
                {input.label && <FormLabel disableFormatting label={input.label} required={input.required} />}
                <LabelAside>
                  <Comment>{nbImages(input)}</Comment>
                  {!!constraints && (
                    <InfoButton
                      type="button"
                      aria-expanded={constraintsOpen}
                      aria-label={formatMessage("common.fileConstraints.openLabel")}
                      $failed={violations.length > 0}
                      onClick={() => toggleConstraints(fieldId)}
                    >
                      {"i"}
                    </InfoButton>
                  )}
                </LabelAside>
              </LabelRow>
              {!!constraints && constraintsOpen && (
                <FileConstraintsPanel constraints={constraints} violations={violations} />
              )}
              <FormField
                key={input.id}
                input={input}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(value, fileProps) => onInputValueChange(input, value as any, fileProps)}
                onMultiImagesChange={(values) => onInputMultiImagesChange(input, values)}
                onViolations={(reported) => reportViolations(fieldId, reported)}
                hasError={inputHasError(input.id) || violations.length > 0}
              />
              {!!input.description && (
                <Description style={inputHasError(input.id) ? { color: theme.colors.error[500] } : {}}>
                  {input.description}
                </Description>
              )}
            </Cell>
          );
        })}
      </InputsContainer>
    </FormWithButtons>
  );
};

export default EnrollmentStepForm;

/**
 * The form definition carries no layout hint, so width is decided by type: pickers and nested
 * collections need the room, plain fields pair up two per row.
 */
const WIDE_INPUT_TYPES: FormInputType[] = [
  FormInputType.File,
  FormInputType.FileContent,
  FormInputType.Image,
  FormInputType.Collection,
  FormInputType.MultiSelect,
];

const isWideInput = (input: { type: FormInputType }) => WIDE_INPUT_TYPES.includes(input.type);

const FILE_TYPES: FormInputType[] = [FormInputType.File, FormInputType.FileContent, FormInputType.Image];

/** What the form says this field will take, when it says anything at all. */
const constraintsOf = (input: { type: FormInputType }): FileConstraints | undefined => {
  if (!FILE_TYPES.includes(input.type)) {
    return undefined;
  }
  const { maxSize, acceptedTypes } = input as FormImageInput;
  return maxSize !== undefined || acceptedTypes?.length ? { maxSize, acceptedTypes } : undefined;
};

const StepTitle = styled(MarkdownText)`
  ${theme.boldText};
  display: block;
  font-size: 1.8rem;
  line-height: 2.875rem;
  align-self: flex-start;
  margin-bottom: 24px;
`;

const Description = styled.div`
  ${theme.text}
  font-size: 0.688rem;
  color: ${theme.colors.gray[700]};
  margin-top: 5px;
`;

const Comment = styled.span`
  ${theme.text}
  font-size: 0.688rem;
  font-style: italic;
  color: #969696;
`;

const InputsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: ${UIConstants.TABLET_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Cell = styled.div<{ $wide?: boolean }>`
  grid-column: ${(props) => (props.$wide ? "1 / -1" : "auto")};

  @media (max-width: ${UIConstants.TABLET_BREAKPOINT}px) {
    grid-column: auto;
  }
`;

const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
`;

const LabelAside = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

/* Hard right in the label, the way the app carries it. */
const InfoButton = styled(UnstyledButton)<{ $failed: boolean }>`
  display: flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => (props.$failed ? theme.colors.error[500] : "#8c8c8c")};
  border-radius: 10px;
  ${theme.mediumText}
  font-size: 0.75rem;
  line-height: 0.9375rem;
  color: ${(props) => (props.$failed ? theme.colors.error[500] : "#8c8c8c")};
`;
